import React from 'react';

const ASEANAIGovernance = () => (
  <article className="news-article-body">
    <p className="news-lead">
      Southeast Asia is home to 680 million people, rapidly digitising economies, and a complex
      patchwork of regulatory traditions. In 2025, ASEAN moved to coordinate AI governance
      across all ten member states — releasing an expanded Generative AI guide, adopting a
      Responsible AI Roadmap 2025–2030, and negotiating a binding
      <strong> Digital Economy Framework Agreement (DEFA)</strong> expected to create the
      region's first legally enforceable AI governance commitments.
    </p>

    <h2>The ASEAN AI Governance Architecture</h2>
    <p>
      ASEAN's AI governance is built in layers — from voluntary principles down to
      sector-specific hard law — reflecting the diverse legal systems and development stages
      across member states:
    </p>
    <div className="news-timeline">
      <div className="news-timeline-item">
        <span className="news-timeline-date">Feb 2024</span>
        <div>
          <strong>ASEAN Guide on AI Governance and Ethics</strong> — Voluntary framework
          establishing seven core principles: transparency, fairness, accountability,
          human-centricity, reliability, privacy, and responsible stewardship.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">January 2025</span>
        <div>
          <strong>Expanded Guide — Generative AI Supplement</strong> — Addresses nine
          policy areas specific to GenAI risks: accountability, data governance, incident
          reporting, testing and assurance, security, content provenance, and AI for public
          good. Identifies six primary GenAI risks: misinformation, deepfakes, IP
          infringement, privacy violations, embedded biases, and anthropomorphism.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">March 2025</span>
        <div>
          <strong>ASEAN Responsible AI Roadmap 2025–2030</strong> — Adopted to coordinate
          member states' national implementation, capacity building, and cross-border
          regulatory interoperability.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">End 2025</span>
        <div>
          <strong>ASEAN AI Safety Network (AI Safe)</strong> — Institutionalised mechanism
          for sharing AI safety best practices, incident data, and governance capacity across
          all ten member states.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">End 2026 (expected)</span>
        <div>
          <strong>ASEAN Digital Economy Framework Agreement (DEFA)</strong> — The region's
          first legally binding AI governance instrument, creating enforceable commitments
          on interoperability, data flows, and AI safety standards across ASEAN.
        </div>
      </div>
    </div>

    <h2>Where Each Member State Stands</h2>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">Vietnam</span>
        <span className="news-spec-value">First comprehensive AI law in SEA — effective March 1, 2026</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Singapore</span>
        <span className="news-spec-value">World's first Agentic AI governance framework (Jan 2026) + AI Verify</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Malaysia</span>
        <span className="news-spec-value">AIGE voluntary framework (Sep 2024) + AI Tech Action Plan 2026–2030</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Indonesia</span>
        <span className="news-spec-value">Two Presidential Regulations (ethics + roadmap) expected 2026; UU PDP active</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Philippines</span>
        <span className="news-spec-value">National AI Strategy 2025–2028 approved; AI Development Act pending</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Thailand</span>
        <span className="news-spec-value">Draft AI Law 2025; AI Governance Center (AIGC) under ETDA active</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Brunei</span>
        <span className="news-spec-value">AI Governance and Ethics Guide released (2025)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Myanmar, Laos, Cambodia</span>
        <span className="news-spec-value">AI strategies in early development; rely on ASEAN framework</span>
      </div>
    </div>

    <h2>The Governance Philosophy: Soft Law First, Hard Law Following</h2>
    <p>
      ASEAN's approach deliberately sequences voluntary frameworks before binding legislation —
      allowing businesses and governments to adapt, experiment, and surface implementation
      challenges before legal enforcement creates rigidity. This "soft law first" model
      contrasts with the EU AI Act's top-down statutory approach.
    </p>
    <p>
      The risk: voluntary frameworks with no enforcement teeth may be ignored by companies
      unwilling to self-regulate. The DEFA's binding commitments, once ratified, will provide
      the enforcement layer the current framework lacks.
    </p>

    <h2>The Deepfake Problem Across SEA</h2>
    <p>
      A unifying concern across all ASEAN governance discussions is <strong>deepfake abuse</strong>
      — AI-generated synthetic media used for financial fraud, political manipulation, and
      non-consensual intimate imagery. Multiple SEA governments have already recorded large-scale
      deepfake fraud incidents involving impersonation of government officials and corporate
      executives. The expanded GenAI guide specifically calls for member states to implement
      <strong>content provenance</strong> standards (watermarking, metadata attribution) as a
      first line of defence.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        ASEAN's layered governance model — voluntary principles now, binding DEFA by end
        of 2026 — is the most pragmatic approach for a region with such diverse legal and
        economic contexts. The critical deadline is DEFA ratification: without it, SEA
        risks becoming a regulatory arbitrage zone where AI applications prohibited elsewhere
        migrate to less-regulated member states. The region's AI governance credibility
        rides on whether DEFA concludes and is implemented by all ten members.
      </p>
    </div>
  </article>
);

export default ASEANAIGovernance;
